import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Deck } from "@/types/linguadeck";
import FlashcardView from "./FlashcardView";
import ExplainModal from "./ExplainModal";

interface QuickStudyModalProps {
  deck: Deck;
  initialCardId: string;
  isTeacher: boolean;
  geminiKey?: string | string[];
  uiLang?: string;
  onClose: () => void;
}

/** Modal for quick study session focused on a specific card (e.g. from Statistics "Repeat" button) */
export default function QuickStudyModal({
  deck,
  initialCardId,
  isTeacher,
  geminiKey,
  uiLang,
  onClose,
}: QuickStudyModalProps) {
  const { t } = useTranslation();
  const [cardIndex, setCardIndex] = useState(0);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [showExplain, setShowExplain] = useState(false);

  const initialStudyDeck = useMemo(() => {
    const cards = deck.cards ?? [];
    const idx = cards.findIndex((c) => c.id === initialCardId);
    if (idx <= 0) return deck;
    const target = cards[idx];
    const rest = cards.filter((_, i) => i !== idx);
    return { ...deck, cards: [target, ...rest] };
  }, [deck, initialCardId]);

  const [studyDeck, setStudyDeck] = useState(initialStudyDeck);
  useEffect(() => {
    setStudyDeck(initialStudyDeck);
    setCardIndex(0);
    setCardFlipped(false);
  }, [initialStudyDeck]);

  const total = studyDeck.cards?.length ?? 0;
  const hasCards = total > 0;

  const handleShuffle = () => {
    const shuffled = [...studyDeck.cards];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setStudyDeck({ ...studyDeck, cards: shuffled });
    setCardIndex(0);
    setCardFlipped(false);
  };

  const handleMark = (correct: boolean) => {
    const nextIndex = (cardIndex + 1) % total;
    setCardIndex(nextIndex);
    setCardFlipped(false);
  };

  const handlePrevCard = () => {
    if (cardIndex <= 0) return;
    setCardIndex(cardIndex - 1);
    setCardFlipped(false);
  };

  const handleNextCard = () => {
    if (cardIndex >= total - 1) return;
    setCardIndex(cardIndex + 1);
    setCardFlipped(false);
  };

  const currentCard = studyDeck.cards[cardIndex];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-[hsl(var(--card-bg))] shadow-2xl"
        style={{ minHeight: 420, maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[hsl(var(--border))] px-4 py-3">
          <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
            {t('quickStudy.title', { title: deck.title })}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--bg3))] hover:text-[hsl(var(--foreground))]"
            aria-label={t("library.close")}
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex flex-1 min-h-0 flex-col items-center justify-center p-6">
          {hasCards ? (
          <FlashcardView
            deck={studyDeck}
            cardIndex={cardIndex}
            cardFlipped={cardFlipped}
            isTeacher={isTeacher}
            onFlip={() => setCardFlipped(!cardFlipped)}
            onMark={handleMark}
            onShuffle={handleShuffle}
            onShowExplain={() => setShowExplain(true)}
            onPrevCard={handlePrevCard}
            onNextCard={handleNextCard}
            onUpdateCard={(idx, updates) => {
              setStudyDeck((prev) => {
                const newCards = prev.cards.map((c, i) => (i === idx ? { ...c, ...updates } : c));
                return { ...prev, cards: newCards };
              });
            }}
          />
          ) : (
            <div className="flex flex-col items-center gap-3 text-center py-8">
              <span className="text-sm text-[hsl(var(--muted-foreground))]">
                {t("statistics.noDifficultWordsData")}
              </span>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-[hsl(var(--primary))] text-white"
              >
                {t("library.close")}
              </button>
            </div>
          )}
        </div>
      </div>

      {showExplain && currentCard && (
        <ExplainModal
          card={currentCard}
          deck={studyDeck}
          uiLang={uiLang ?? "en"}
          onClose={() => setShowExplain(false)}
        />
      )}
    </div>
  );
}
